import { AnimatePresence, m } from "framer-motion";
import { CircleCheck, CircleX } from "lucide-react";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../cn";
import { useMotionTransition } from "../motion";

/**
 * Minimal, self-contained toast system for admin-v2.
 *
 * This is the first transient-feedback primitive in the app: mutations (mark
 * handled, reply, archive, ...) raise a short success/error message that
 * dismisses itself. It is intentionally headless and dependency-free (no
 * `sonner`) so it stays on the app's Tailwind tokens and motion setup. The
 * public surface is deliberately tiny — `toast.success(msg)` / `toast.error(msg)`
 * — and grows only when a caller needs more.
 */

type ToastTone = "success" | "error";

interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
}

/** How long a toast stays on screen before it auto-dismisses. */
const TOAST_TTL_MS = 3400;

const ToastContext = createContext<ToastApi | null>(null);

const toneMeta: Record<
  ToastTone,
  { icon: typeof CircleCheck; accent: string }
> = {
  success: { icon: CircleCheck, accent: "text-success" },
  error: { icon: CircleX, accent: "text-destructive" },
};

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastTransition = useMotionTransition();
  const nextId = useRef(0);
  // Track pending auto-dismiss timers so we can clear them on manual dismiss
  // and on unmount — otherwise a fired callback would call setToasts on an
  // unmounted provider (tests, hot reload, future conditional mounting).
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), TOAST_TTL_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) {
        clearTimeout(timer);
      }
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push("success", message),
      error: (message: string) => push("error", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed right-5 bottom-5 z-50 flex w-[min(22rem,calc(100vw-2.5rem))] flex-col gap-2"
        >
          <AnimatePresence initial={false}>
            {toasts.map((toast) => {
              const { icon: Icon, accent } = toneMeta[toast.tone];
              return (
                <m.div
                  key={toast.id}
                  layout
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={toastTransition}
                  className="pointer-events-auto flex items-start gap-2.5 rounded-xl border border-border bg-surface p-3 shadow-md"
                >
                  <Icon
                    className={cn("mt-0.5 size-[1.1rem] shrink-0", accent)}
                    aria-hidden="true"
                  />
                  <p className="text-sm leading-snug text-foreground">
                    {toast.message}
                  </p>
                </m.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </ToastContext.Provider>
  );
}

/**
 * Access the toast API. Must be called under a {@link ToastProvider}; throwing
 * otherwise surfaces the missing-provider mistake at render time instead of
 * silently dropping feedback.
 */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
